import { useRef, useState } from 'react';

import {
  ApiError,
  clearLibrary,
  exportLibraryFile,
  importLibraryFile,
  type LibraryImportResult,
} from '../../api/client';

interface LibraryToolsProps {
  count: number;
  onChanged: () => void;
}

/**
 * Sauvegarde, restauration et purge de la bibliothèque.
 *
 * L'export produit un fichier JSON, l'import le relit. Ils existent parce que
 * la bibliothèque est le seul contenu du produit qui ne se reconstruit pas :
 * une recherche se relance, un verdict humain ne se retrouve pas.
 *
 * Jumeau de `src/ui/components/LibraryTools.tsx`. Les deux interfaces ne
 * partagent pas leurs classes, et un composant qui prendrait un jeu de classes
 * en paramètre serait pire que ce doublon : il faudrait le lire deux fois pour
 * savoir à quoi ressemble l'un des deux écrans. Les appels d'API, eux, ne sont
 * pas dupliqués — ils viennent tous de `src/api/client.ts`.
 */
export function LibraryTools({ count, onChanged }: LibraryToolsProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'export' | 'import' | 'purge' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function handleExport(): Promise<void> {
    setBusy('export');
    setError(null);
    setMessage(null);
    try {
      const { content, fileName } = await exportLibraryFile();
      const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      setMessage(`Sauvegarde téléchargée — ${fileName}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "L'export a échoué.");
    } finally {
      setBusy(null);
    }
  }

  async function handleImport(file: File): Promise<void> {
    setBusy('import');
    setError(null);
    setMessage(null);
    try {
      const result: LibraryImportResult = await importLibraryFile(await file.text(), false);
      setMessage(
        `${result.added} ajoutée${result.added > 1 ? 's' : ''}, ${result.updated} mise${result.updated > 1 ? 's' : ''} à jour, ${result.skipped} ignorée${result.skipped > 1 ? 's' : ''}.`,
      );
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "L'import a échoué.");
    } finally {
      setBusy(null);
      if (fileInput.current !== null) fileInput.current.value = '';
    }
  }

  async function handlePurge(): Promise<void> {
    setBusy('purge');
    setError(null);
    setMessage(null);
    try {
      const removed = await clearLibrary();
      setMessage(`${removed} fiche${removed > 1 ? 's' : ''} retirée${removed > 1 ? 's' : ''}.`);
      setConfirming(false);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'La purge a échoué.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="tools">
      <div className="tools-row">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => void handleExport()}
          disabled={busy !== null || count === 0}
        >
          {busy === 'export' ? 'Export…' : 'Sauvegarder'}
        </button>

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => fileInput.current?.click()}
          disabled={busy !== null}
        >
          {busy === 'import' ? 'Import…' : 'Restaurer'}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void handleImport(file);
          }}
        />

        {count > 0 ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setConfirming((value) => !value)}
            disabled={busy !== null}
          >
            Vider
          </button>
        ) : null}
      </div>

      {/* La purge est irréversible côté bibliothèque : elle se confirme, et le
          message dit ce qui survit — la mémoire « déjà traité ». */}
      {confirming ? (
        <div className="notice notice-error" style={{ marginTop: 10 }}>
          <span style={{ flex: 1, minWidth: '18ch' }}>
            Retirer les {count} fiches ? Les établissements resteront reconnus comme « déjà
            traités » lors des prochaines recherches.
          </span>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => void handlePurge()}
            disabled={busy !== null}
          >
            {busy === 'purge' ? 'Purge…' : 'Confirmer'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setConfirming(false)}
          >
            Annuler
          </button>
        </div>
      ) : null}

      {error !== null ? (
        <p className="notice notice-error" role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      ) : null}

      {message !== null ? (
        <p className="notice" role="status" style={{ marginTop: 10 }}>
          {message}
        </p>
      ) : null}
    </div>
  );
}
